import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaClipboardList, FaPaperPlane, FaWarehouse } from 'react-icons/fa';
import { bookAPI, distributionAPI } from '../../utils/api';

const emptyItem = { bookId: '', quantity: '' };

export default function DistributionRequisitionsPage() {
  const [rows, setRows] = useState([]);
  const [books, setBooks] = useState([]);
  const [stock, setStock] = useState([]);
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const stockByBook = useMemo(() => {
    const map = {};
    stock.forEach(row => { map[row.book_id || row.bookId] = Number(row.available_qty || row.availableQty || 0); });
    return map;
  }, [stock]);

  const load = async () => {
    setLoading(true);
    try {
      const [reqRes, bookRes, stockRes] = await Promise.all([
        distributionAPI.requisitions.list(),
        bookAPI.getAll(),
        distributionAPI.stockSummary(),
      ]);
      setRows(reqRes.data?.content || reqRes.data || []);
      setBooks(bookRes.data?.content || bookRes.data || []);
      setStock(stockRes.data?.content || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setItem = (index, patch) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
  };

  const submit = async e => {
    e.preventDefault();
    const cleanItems = items
      .map(item => ({ bookId: Number(item.bookId), quantity: Number(item.quantity) }))
      .filter(item => item.bookId && item.quantity > 0);
    if (!cleanItems.length) {
      setError('Add at least one book and quantity.');
      return;
    }
    setSubmitting(true);
    try {
      await distributionAPI.requisitions.create({ items: cleanItems, remarks });
      setItems([{ ...emptyItem }]);
      setRemarks('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const card = (icon, label, value) => (
    <div className="eduss-dashboard-card eduss-dashboard-card-green">
      <div className="eduss-dashboard-card-icon">{icon}</div>
      <div className="eduss-dashboard-card-content">
        <div className="eduss-dashboard-card-label">{label}</div>
        <div className="eduss-dashboard-card-value">{loading ? '...' : value}</div>
      </div>
    </div>
  );

  return (
    <div className="eduss-dashboard">
      <div className="eduss-dashboard-welcome">
        <h1>Warehouse Requisitions</h1>
        <p>Request books from warehouse based on assigned area demand and available accepted stock.</p>
      </div>

      <div className="eduss-dashboard-cards-row">
        {card(<FaWarehouse />, 'Accepted Stock Lines', stock.length)}
        {card(<FaClipboardList />, 'My Requests', rows.length)}
        {card(<FaCheckCircle />, 'Approved', rows.filter(r => r.status === 'APPROVED').length)}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="eduss-dashboard-content">
        <h2>New Request</h2>
        <form onSubmit={submit}>
          {items.map((item, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px', gap: 12, marginBottom: 10 }}>
              <select className="form-control" value={item.bookId} onChange={e => setItem(index, { bookId: e.target.value })}>
                <option value="">Select book</option>
                {books.map(book => (
                  <option key={book.id} value={book.id}>
                    {book.title || book.name || `Book #${book.id}`} - Stock {stockByBook[book.id] || 0}
                  </option>
                ))}
              </select>
              <input className="form-control" type="number" min="1" value={item.quantity} placeholder="Qty" onChange={e => setItem(index, { quantity: e.target.value })} />
              <button className="btn btn-outline-secondary" type="button" onClick={() => setItems(prev => prev.filter((_, i) => i !== index))} disabled={items.length === 1}>
                Remove
              </button>
            </div>
          ))}
          <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => setItems(prev => [...prev, { ...emptyItem }])}>
            Add Item
          </button>
          <textarea className="form-control mt-3" rows="3" value={remarks} placeholder="Remarks" onChange={e => setRemarks(e.target.value)} />
          <button className="eduss-home-cta mt-3" type="submit" disabled={submitting}>
            <FaPaperPlane /> {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>

      <div className="eduss-dashboard-content">
        <h2>Request History</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="mini-info-table" style={{ width: '100%' }}>
            <thead><tr><th>Reference</th><th>Status</th><th>Items</th><th>Remarks</th></tr></thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{row.reference_no}</td>
                  <td>{row.status}</td>
                  <td>{(row.items || []).reduce((sum, item) => sum + Number(item.requested_qty || 0), 0).toLocaleString()}</td>
                  <td>{row.remarks || '-'}</td>
                </tr>
              ))}
              {!loading && !rows.length && <tr><td colSpan="4">No requisitions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
